import type { ObjectId } from 'mongodb';

import type { EquipmentType } from './EnergyBackup';
import type { Meta } from './Entities';
import type { IsoDateString } from './Helpers';
import type { ProgramType } from './ProgramTypes';

export enum PromoType {
  incentive = 'incentive',
  discount = 'discount',
  referralCredit = 'referralCredit',
}

export enum PromoParty {
  homeowner = 'homeowner',
  installer = 'installer',
  financier = 'financier', // Palmetto
}

export enum PromoValueType {
  fixed = 'fixed', // a dollar amount
  percentOfCost = 'percentOfCost', // a fraction (0 to 1) of the qualifying equipment cost
  perWatt = 'perWatt', // dollars per watt of system size
}

export interface PromoValueRuleConfig {
  type: PromoValueType;
  value: number;
  tiers?: Array<{
    from: number;
    to?: number;
    type: PromoValueType;
    value: number;
  }>;
  match?: {
    equipmentTypes?: EquipmentType[];
    manufacturers?: string[];
    modelNumbers?: string[];
  };
  min?: number;
  max?: number;
}

export interface PromoUtilityScope {
  state: string; // one entry per state
  lseIds: number[]; // non-empty; every id must belong to this state
}

export interface PromoDocument {
  id: ObjectId;
  type: PromoType; // incentive-type promos get the claim workflow
  code: string; // shown to admins and reusable across promos; the document id is the key, e.g. 'SGIP-RES'
  name: string;
  utilities: PromoUtilityScope[]; // where the promo exists (non-empty); organization entries must fall within these
  programTypes: ProgramType[];
  collectableBy: PromoParty[]; // who may collect it ("who can obtain it"); required and non-empty for incentive-type promos, ignored for other types
  valueRule: PromoValueRuleConfig;
  exclusiveGroup?: string; // not used yet
  stackable?: boolean; // not used yet
  validFrom: IsoDateString; // required; first day the promo is valid (UTC)
  validThrough?: IsoDateString; // last day the promo is valid (UTC, inclusive); omitted means no end date
  meta?: Meta; // soft delete uses meta.status
}

export interface CreatePromoRequest {
  type: PromoType;
  code: string;
  name: string;
  utilities: PromoUtilityScope[];
  programTypes: ProgramType[];
  collectableBy?: PromoParty[];
  valueRule: PromoValueRuleConfig;
  validFrom: IsoDateString;
  validThrough?: IsoDateString;
}

export interface UpdatePromoRequest {
  name?: string;
  utilities?: PromoUtilityScope[];
  validThrough?: IsoDateString;
}

export interface PromoUtility {
  lseId: number;
  name: string;
  state: string;
}

export interface PromoListResponse {
  promos: PromoDocument[];
  utilities: PromoUtility[];
  total: number;
}
